/*
  이 파일은 Vue.js 애플리케이션에서 사용자 인증 관련 기능을 관리하는 커스텀 훅 (Composable)입니다.
  로그인, 로그아웃, 회원가입, 세션 확인 등 주요 인증 기능을 제공하며,
  사용자 상태 (`isAuthenticated`, `user`)를 전역에서 반응형으로 사용할 수 있도록 `ref`를 활용해 구현합니다.
  로그인 시에는 서버로 API 요청을 보내고, 응답으로 받은 사용자 정보를 세션 스토리지에 저장하고 전역 상태를 업데이트합니다.
  로그아웃 시에는 사용자 정보를 제거하고, 로컬 스토리지에 캐시된 보고서 데이터를 초기화합니다.
  이 모듈은 인증 관련 로직을 모듈화하여 재사용성과 유지보수성을 높이기 위한 목적으로 설계되었습니다.
*/

import { ref } from 'vue'
import { restApiClient } from '@/api/apiConfig'

// 세션 스토리지에서 사용자 정보를 불러오는 유틸리티 함수
const loadUserFromStorage = () => {
  const storedUser = sessionStorage.getItem('user')
  return storedUser ? JSON.parse(storedUser) : null
}

// 전역 인증 상태 정의
const isAuthenticated = ref(Boolean(loadUserFromStorage()))
const user = ref(loadUserFromStorage())
const userName = ref(null)

export function useAuth() {
  // 로그인 함수: 이메일과 비밀번호로 인증 요청 후 사용자 정보를 저장
  const login = async (email, password) => {
    try {
      // 백엔드에 로그인 API 요청 (POST)
      const response = await restApiClient.post('/restapi/auth/login', {
        email,
        password
      })
      const userData = {
        id: response.userId,
        language: response.language
      }
      // 세션 스토리지에 사용자 정보 저장
      sessionStorage.setItem('user', JSON.stringify(userData));

      // 전역 상태 업데이트
      isAuthenticated.value = true;
      user.value = userData;
      
      // 사용자 이름 가져오기
      await fetchUserName();
      
      return true;

    } catch (error) {
      console.error('[오류] 로그인 요청 실패:', error)
      console.error('오류 상세:', error?.response?.data || error?.message)
      return false
    }
  }


  // 회원가입 함수: 사용자 정보를 전달해 계정 생성 요청
  const signup = async (signupData) => {
    try {
      const response = await restApiClient.post('/restapi/auth/signup', signupData);
      console.log(response);
      return {
        success: true,
        message: '회원가입이 완료되었습니다. 로그인해주세요.'
      };
    } catch (error) {
      console.error('회원가입 오류:', error)
      return {
        success: false,
        message: error?.message || error?.response?.data?.message || '회원가입 요청 중 오류가 발생했습니다.'
      }
    }
  }

  // 로그아웃 함수: 사용자 세션 제거 및 상태 초기화
  const logout = async () => {
    // 세션에서 사용자 정보 제거
    sessionStorage.removeItem('user')

    // 회의 관련 세션 데이터 정리
    sessionStorage.removeItem('currentMeetingCode')
    sessionStorage.removeItem('currentMeetingId')

    const response = await restApiClient.post('/restapi/auth/logout')
    console.log(response)
    
    alert('로그아웃 완료')

    // 로컬 스토리지에서 캐시된 보고서 데이터 삭제
    clearReportCache()

    // 전역 상태 초기화
    isAuthenticated.value = false
    user.value = null
    userName.value = null
  }

  // 사용자 이름 가져오기 함수
  const fetchUserName = async () => {
    try {
      const response = await restApiClient.get('/restapi/users/name')
      userName.value = response.name
      return response.name
    } catch (error) {
      console.error('[오류] 사용자 이름 조회 실패:', error)
      userName.value = null
      return null
    }
  }

  // 보고서 캐시 삭제 유틸리티: localStorage의 report_cache 항목 제거
  const clearReportCache = () => {
    const CACHE_PREFIX = 'report_cache_'
    const REGISTRY_KEY = 'report_cache_registry'

    // 레지스트리 로딩 시 예외 방지
    let registry = {}
    try {
      const data = localStorage.getItem(REGISTRY_KEY)
      registry = data ? JSON.parse(data) : {}
    } catch {
      registry = {}
    }

    // 등록된 모든 캐시 키 제거
    Object.keys(registry).forEach(key => {
      if (key.startsWith(CACHE_PREFIX)) {
        localStorage.removeItem(key)
      }
    })

    // 레지스트리 초기화
    localStorage.setItem(REGISTRY_KEY, JSON.stringify({}))
  }


  return {
    isAuthenticated,
    user,
    userName,
    login,
    logout,
    signup,
    fetchUserName
  }
}